import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Plus, Trash2, CalendarDays, Repeat, X } from "lucide-react";
import { toast } from "sonner";
import { api, COLORS } from "@/lib/api";
import { inputCls, btnPrimary, btnSecondary, cardCls, CheckCircle } from "@/components/shared";
import { LinkedShopping } from "@/components/todo/LinkedShopping";
import { celebrateIfDone } from "@/lib/celebrate";
import { SortableList, useSortableRow, DragHandle, reorderItems } from "@/components/Sortable";

const FILTERS = ["all", "active", "completed"];
const REPEAT = { none: "No repeat", daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

const DueDateField = ({ due, setDue, show, setShow }) =>
  show ? (
    <div className="relative md:w-48 flex items-center" data-testid="todo-item-due-field">
      <input data-testid="todo-item-due-input" type="date" autoFocus className={`${inputCls} pr-9`} value={due} onChange={(e) => setDue(e.target.value)} />
      <button type="button" aria-label="Remove date" data-testid="todo-item-due-remove" onClick={() => { setDue(""); setShow(false); }}
        className="absolute right-2 p-1 rounded-md text-t-faint hover:text-t-ink"><X className="h-4 w-4" /></button>
    </div>
  ) : (
    <button type="button" data-testid="todo-item-add-date-button" onClick={() => setShow(true)} className={`${btnSecondary} whitespace-nowrap`}>
      <CalendarDays className="h-4 w-4" /> Add date
    </button>
  );

const AddItemForm = ({ onAdd }) => {
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [showDue, setShowDue] = useState(false);
  const [recurrence, setRecurrence] = useState("none");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await onAdd({ text: text.trim(), due_date: due || null, recurrence });
    setText("");
    setDue("");
    setShowDue(false);
    setRecurrence("none");
  };
  return (
    <form onSubmit={submit} className="flex flex-col md:flex-row gap-3" data-testid="add-todo-item-form">
      <input data-testid="todo-item-text-input" className={`${inputCls} flex-1`} placeholder="Add a task..." value={text} onChange={(e) => setText(e.target.value)} />
      <DueDateField due={due} setDue={setDue} show={showDue} setShow={setShowDue} />
      <select data-testid="todo-item-recurrence-select" className={`${inputCls} md:w-36`} value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
        {Object.entries(REPEAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button type="submit" data-testid="add-todo-item-button" className={btnPrimary}><Plus className="h-4 w-4" /> Add</button>
    </form>
  );
};

const TodoRow = ({ item, color, onToggle, onDelete, onRepeat, onDate }) => {
  const s = useSortableRow(item.id);
  return (
  <li ref={s.ref} style={s.style} data-testid={`todo-item-${item.id}`}
    className={`group flex items-center gap-3 py-4 border-b border-t-surface last:border-0 ${s.isDragging ? "bg-t-card shadow-lg rounded-xl px-2" : ""}`}>
    <DragHandle handle={s.handle} testId={`todo-item-drag-${item.id}`} />
    <CheckCircle checked={item.done} onClick={onToggle} color={color} testId={`todo-item-check-${item.id}`} />
    <div className="flex-1 min-w-0">
      <p className={`text-[15px] transition-colors duration-300 ${item.done ? "line-through text-t-faint" : "text-t-ink"}`}>{item.text}</p>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <label className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-pointer ${
          item.due_date ? "border-t-line text-t-muted" : "border-transparent text-t-faint opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        }`} data-testid={`todo-item-date-${item.id}`}>
          <CalendarDays className="h-3 w-3" /> {item.due_date ? format(parseISO(item.due_date), "MMM d") : "Add date"}
          <input type="date" data-testid={`todo-item-date-input-${item.id}`} value={item.due_date || ""} onChange={(e) => onDate(e.target.value || null)}
            className="absolute inset-0 opacity-0 cursor-pointer w-full" />
        </label>
        <label className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-pointer ${
          item.recurrence !== "none" ? "bg-t-c5-soft text-t-c5-ink border-t-c5-line" : "border-transparent text-t-faint opacity-0 group-hover:opacity-100"
        }`}>
          <Repeat className="h-3 w-3" />
          <select data-testid={`todo-item-repeat-${item.id}`} value={item.recurrence} onChange={(e) => onRepeat(e.target.value)}
            className="bg-transparent appearance-none cursor-pointer focus:outline-none">
            {Object.entries(REPEAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>
    </div>
    <button data-testid={`delete-todo-item-${item.id}`} onClick={onDelete} className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-t-primary-soft text-t-danger transition-opacity">
      <Trash2 className="h-4 w-4" />
    </button>
  </li>
  );
};

export const TodoPanel = ({ list, onChange, shops }) => {
  const [filter, setFilter] = useState("all");
  const color = COLORS[list.color] || COLORS.terracotta;
  const base = `/todo-lists/${list.id}/items`;
  const add = async (body) => onChange((await api.post(base, body)).data);
  const toggle = async (it) => {
    const updated = (await api.patch(`${base}/${it.id}`, { done: !it.done })).data;
    onChange(updated);
    celebrateIfDone(list, updated);
    if (!it.done && it.recurrence !== "none" && !it.spawned) toast.success(`Nice! Next "${it.text}" scheduled`);
  };
  const setRepeat = async (it, recurrence) => onChange((await api.patch(`${base}/${it.id}`, { recurrence })).data);
  const setDate = async (it, due_date) => onChange((await api.patch(`${base}/${it.id}`, { due_date })).data);
  const remove = async (it) => onChange((await api.delete(`${base}/${it.id}`)).data);
  const items = list.items.filter((i) => (filter === "all" ? true : filter === "active" ? !i.done : i.done));
  const pct = list.items.length ? Math.round((list.items.filter((i) => i.done).length / list.items.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className={`${cardCls} p-6 sm:p-8 fade-up`} data-testid="todo-panel">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold" data-testid="todo-panel-title">{list.name}</h2>
            <p className="text-sm text-t-muted mt-1" data-testid="todo-panel-progress">{pct}% complete</p>
          </div>
          <div className="flex bg-t-surface rounded-xl p-1">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`todo-filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === f ? "bg-t-card shadow-sm text-t-ink" : "text-t-muted"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="h-1.5 bg-t-surface rounded-full mb-6 overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <AddItemForm onAdd={add} />
        <SortableList ids={items.map((i) => i.id)} onReorder={(a, o) => reorderItems(`/todo-lists/${list.id}`, list, a, o, onChange)}>
          <ul className="mt-4">
            {items.map((it) => (
              <TodoRow key={it.id} item={it} color={color} onToggle={() => toggle(it)} onDelete={() => remove(it)} onRepeat={(r) => setRepeat(it, r)} onDate={(d) => setDate(it, d)} />
            ))}
          </ul>
        </SortableList>
        {!items.length && <p className="text-sm text-t-faint text-center py-8" data-testid="todo-items-empty">Nothing here yet.</p>}
      </div>
      <LinkedShopping list={list} shops={shops} />
    </div>
  );
};
